"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.findGlobalFormula = exports.globalFormulaCatalog = void 0;
const metric_catalog_1 = require("./metric-catalog");
const metric = (metricKey) => ({ kind: 'metric', metric: metricKey });
const number = (value) => ({ kind: 'number', value });
const percentage = (numeratorMetric, denominatorMetric) => ({
    kind: 'multiply',
    left: {
        kind: 'divide',
        left: metric(numeratorMetric),
        right: metric(denominatorMetric),
    },
    right: number(100),
});
const freezeFormulaExpression = (expression) => {
    switch (expression.kind) {
        case 'metric':
        case 'number':
            return Object.freeze({ ...expression });
        case 'convertCurrency':
            return Object.freeze({
                ...expression,
                expression: freezeFormulaExpression(expression.expression),
            });
        case 'add':
        case 'subtract':
        case 'multiply':
        case 'divide':
            return Object.freeze({
                ...expression,
                left: freezeFormulaExpression(expression.left),
                right: freezeFormulaExpression(expression.right),
            });
    }
};
const freezeGlobalFormula = (formula) => Object.freeze({
    ...formula,
    inputMetrics: Object.freeze([...formula.inputMetrics]),
    ...(formula.expression === undefined ? {} : { expression: freezeFormulaExpression(formula.expression) }),
});
/**
 * The initial global KPI set mirrors existing compiler behavior exactly.
 * Keeping these keys stable lets current V1 definitions retain their results
 * while making their derivation explicit to the catalog and UI.
 */
const globalFormulaDefinitions = [
    {
        key: 'workforce.employees.growth_1y',
        displayName: 'Employee growth, 1 year',
        semanticVersion: 'v1',
        kind: 'year_over_year',
        inputMetrics: ['workforce.employees.current'],
    },
    {
        key: 'financial.revenue.growth_1y',
        displayName: 'Revenue growth, 1 year',
        semanticVersion: 'v1',
        kind: 'year_over_year',
        inputMetrics: ['financial.revenue.current'],
    },
    {
        key: 'financial.revenue.per_employee',
        displayName: 'Revenue per employee',
        semanticVersion: 'v1',
        kind: 'arithmetic',
        inputMetrics: ['financial.revenue.current', 'workforce.employees.current'],
        expression: {
            kind: 'divide',
            left: metric('financial.revenue.current'),
            right: metric('workforce.employees.current'),
        },
    },
    {
        key: 'workforce.employees.per_active_workplace',
        displayName: 'Employees per active workplace',
        semanticVersion: 'v1',
        kind: 'arithmetic',
        inputMetrics: ['workforce.employees.current', 'company.workplaces.active_count'],
        expression: {
            kind: 'divide',
            left: metric('workforce.employees.current'),
            right: metric('company.workplaces.active_count'),
        },
    },
    {
        key: 'hiring.active_jobposts.per_employee',
        displayName: 'Active job postings per employee',
        semanticVersion: 'v1',
        kind: 'arithmetic',
        inputMetrics: ['hiring.active_jobposts.current', 'workforce.employees.current'],
        expression: {
            kind: 'divide',
            left: metric('hiring.active_jobposts.current'),
            right: metric('workforce.employees.current'),
        },
    },
    {
        key: 'financial.gross_profit_margin.current',
        displayName: 'Gross profit margin (%)',
        semanticVersion: 'v1',
        kind: 'arithmetic',
        inputMetrics: ['financial.gross_profit.current', 'financial.revenue.current'],
        expression: percentage('financial.gross_profit.current', 'financial.revenue.current'),
    },
    {
        key: 'financial.ebit_margin.current',
        displayName: 'EBIT margin (%)',
        semanticVersion: 'v1',
        kind: 'arithmetic',
        inputMetrics: ['financial.ebit.current', 'financial.revenue.current'],
        expression: percentage('financial.ebit.current', 'financial.revenue.current'),
    },
    {
        key: 'financial.ebitda_margin.current',
        displayName: 'EBITDA margin (%)',
        semanticVersion: 'v1',
        kind: 'arithmetic',
        inputMetrics: ['financial.ebitda.current', 'financial.revenue.current'],
        expression: percentage('financial.ebitda.current', 'financial.revenue.current'),
    },
    {
        key: 'financial.pre_tax_profit_margin.current',
        displayName: 'Pre-tax profit margin (ratio)',
        semanticVersion: 'v1',
        kind: 'arithmetic',
        inputMetrics: ['financial.pre_tax_profit.current', 'financial.revenue.current'],
        expression: {
            kind: 'divide',
            left: metric('financial.pre_tax_profit.current'),
            right: metric('financial.revenue.current'),
        },
    },
    {
        key: 'financial.net_profit_margin.current',
        displayName: 'Net profit margin (ratio)',
        semanticVersion: 'v1',
        kind: 'arithmetic',
        inputMetrics: ['financial.net_profit.current', 'financial.revenue.current'],
        expression: {
            kind: 'divide',
            left: metric('financial.net_profit.current'),
            right: metric('financial.revenue.current'),
        },
    },
    {
        key: 'financial.equity_ratio.current',
        displayName: 'Equity ratio (%)',
        semanticVersion: 'v1',
        kind: 'arithmetic',
        inputMetrics: ['financial.total_equity.current', 'financial.total_assets.current'],
        expression: percentage('financial.total_equity.current', 'financial.total_assets.current'),
    },
    {
        key: 'financial.current_ratio.current',
        displayName: 'Current ratio',
        semanticVersion: 'v1',
        kind: 'arithmetic',
        inputMetrics: ['financial.total_current_assets.current', 'financial.total_current_liabilities.current'],
        expression: {
            kind: 'divide',
            left: metric('financial.total_current_assets.current'),
            right: metric('financial.total_current_liabilities.current'),
        },
    },
    {
        key: 'financial.net_debt_to_ebit.current',
        displayName: 'Net debt / EBIT',
        semanticVersion: 'v1',
        kind: 'arithmetic',
        inputMetrics: ['financial.net_debt.current', 'financial.ebit.current'],
        expression: {
            kind: 'divide',
            left: metric('financial.net_debt.current'),
            right: metric('financial.ebit.current'),
        },
    },
    {
        key: 'financial.net_debt_to_ebitda.current',
        displayName: 'Net debt / EBITDA',
        semanticVersion: 'v1',
        kind: 'arithmetic',
        inputMetrics: ['financial.net_debt.current', 'financial.ebitda.current'],
        expression: {
            kind: 'divide',
            left: metric('financial.net_debt.current'),
            right: metric('financial.ebitda.current'),
        },
    },
    {
        key: 'financial.net_debt_to_equity.current',
        displayName: 'Net debt / equity',
        semanticVersion: 'v1',
        kind: 'arithmetic',
        inputMetrics: ['financial.net_debt.current', 'financial.total_equity.current'],
        expression: {
            kind: 'divide',
            left: metric('financial.net_debt.current'),
            right: metric('financial.total_equity.current'),
        },
    },
    {
        key: 'financial.enterprise_value_to_ebit.current',
        displayName: 'Enterprise value / EBIT',
        semanticVersion: 'v1',
        kind: 'arithmetic',
        inputMetrics: ['financial.enterprise_value.current', 'financial.ebit.current'],
        expression: {
            kind: 'divide',
            left: metric('financial.enterprise_value.current'),
            right: metric('financial.ebit.current'),
        },
    },
    {
        key: 'financial.enterprise_value_to_ebitda.current',
        displayName: 'Enterprise value / EBITDA',
        semanticVersion: 'v1',
        kind: 'arithmetic',
        inputMetrics: ['financial.enterprise_value.current', 'financial.ebitda.current'],
        expression: {
            kind: 'divide',
            left: metric('financial.enterprise_value.current'),
            right: metric('financial.ebitda.current'),
        },
    },
    {
        key: 'financial.enterprise_value_to_revenue.current',
        displayName: 'Enterprise value / revenue',
        semanticVersion: 'v1',
        kind: 'arithmetic',
        inputMetrics: ['financial.enterprise_value.current', 'financial.revenue.current'],
        expression: {
            kind: 'divide',
            left: metric('financial.enterprise_value.current'),
            right: metric('financial.revenue.current'),
        },
    },
    ...metric_catalog_1.realtimeApolloHeadcountGrowthMetrics.map(({ key, displayName, currentMetricKey, historicalMetricKey }) => ({
        key,
        displayName,
        semanticVersion: 'v1',
        kind: 'apollo_headcount_growth',
        inputMetrics: [currentMetricKey, historicalMetricKey],
    })),
    ...metric_catalog_1.realtimeLinkedInEmployeeGrowthMetrics.map(({ key, displayName }) => ({
        key,
        displayName,
        semanticVersion: 'v1',
        kind: 'linkedin_employee_growth',
        inputMetrics: ['linkedin.profile.employee_count'],
    })),
];
/**
 * Immutable KPI semantics shared by API and authoring clients.
 *
 * Saved filters refer to the stable `key`; semantic changes must be introduced
 * as a new key/version rather than mutating these definitions at runtime.
 */
exports.globalFormulaCatalog = Object.freeze(globalFormulaDefinitions.map(freezeGlobalFormula));
/** Finds a code-owned global KPI without exposing a mutable catalog. */
const findGlobalFormula = (key) => exports.globalFormulaCatalog.find((formula) => formula.key === key);
exports.findGlobalFormula = findGlobalFormula;
//# sourceMappingURL=global-formula-catalog.js.map